/**
 * Email and bare-domain scrubbing for Sentry payloads.
 *
 * Split from the event-shaping policy in `sentry.ts` because this is the layer
 * that decides what a domain IS - the patterns, the filename carve-out, the
 * depth bound - while that file decides which parts of an event get walked. The
 * dependency runs one way only: nothing here knows about Sentry event shapes
 * beyond the two collection types it iterates.
 */

import { redactOwnDataProperty, toWritableClone } from '@/lib/telemetry-redaction';

import type { ErrorEvent } from '@sentry/react';

/** Email addresses are constituent-adjacent PII. A Postgres `23505`
 * unique-violation message echoes the offending value verbatim, and CRM org
 * records key on company domains — both surface raw emails in error text.
 * Bounded character classes only — no nested quantifiers — to stay linear-time
 * (ReDoS prevention is a hard project rule). */
const EMAIL_PATTERN = /\b[\w%+.-]+@[\d.A-Za-z-]+\.[A-Za-z]{2,}\b/g;

/** Bare hostnames/domains (`health.nsw.gov.au`, `acme.com`) are themselves the
 * CRM org identity for this platform — leaking them into telemetry leaks the
 * constituent's organisation. Requires 1+ dot-separated label then an alphabetic
 * final label of length >= 2, so version numbers (`1.2.3`) and sentence prose
 * survive untouched. Each repeated label is prefixed by a literal `.`, so
 * consecutive iterations cannot overlap and the match is provably linear with no
 * backtracking blowup — ReDoS prevention is a hard project rule. Lengths are
 * bounded to DNS limits (63 chars/label). */
// safe-regex flags any optional inner group adjacent to a similar trailing atom
// as ambiguous, but every quantifier here is finitely bounded ({0,15}, {1,63}),
// so the worst case is ~15x linear, not exponential — measured linear at 7ms for
// 200k chars of adversarial `a.1.1.1…` input. The heuristic cannot see the bounds.
// eslint-disable-next-line security/detect-unsafe-regex
const DOMAIN_PATTERN = /\b[\dA-Za-z-]{1,63}(?:\.[\dA-Za-z-]{1,63}){0,15}\.[A-Za-z]{2,63}\b/g;

/** Source-file and bundle extensions that share a domain's shape (`App.tsx`,
 * `user.service.ts`, `chunk.mjs`) but are never real TLDs. Skipping them keeps
 * stack-frame filenames — the most actionable Sentry signal — intact while
 * still redacting genuine domains. None of these is an assigned TLD, so the
 * carve-out costs zero PII coverage. */
const CODE_FILE_EXTENSIONS = new Set([
  'ts',
  'tsx',
  'js',
  'jsx',
  'mjs',
  'cjs',
  'json',
  'css',
  'scss',
  'less',
  'html',
  'htm',
  'map',
  'vue',
  'svelte',
  'wasm',
]);

/** Scrubs email addresses and bare domains from free-text strings before they
 * reach Sentry. Emails are redacted first so the local-part is not left dangling
 * once its domain is replaced. A candidate whose final label is a code-file
 * extension is left untouched (it's a filename, not a domain). */
export function redactDomainsFromText(text: string): string {
  return text.replaceAll(EMAIL_PATTERN, '[email]').replaceAll(DOMAIN_PATTERN, (match) => {
    const finalLabel = match.slice(match.lastIndexOf('.') + 1).toLowerCase();
    return CODE_FILE_EXTENSIONS.has(finalLabel) ? match : '[domain]';
  });
}

/** Recursively scrubs email/domain PII from every string leaf of a structured
 * value (objects/arrays). The free-text scrubbers above only reach `message`,
 * exception values, and breadcrumb messages — but a careless `setExtra({ domain })`
 * or `captureException(err, { contexts })` routes a raw constituent domain through
 * `event.extra` / `event.contexts` / `event.request.data` / `event.user`, which
 * ship to Sentry un-redacted otherwise. Mutable objects/arrays are scrubbed in
 * place; a frozen container is scrubbed on a clone (see `toWritableClone`). Always
 * returns the value to scrub — a scalar leaf, or the swapped-in clone — so the
 * caller reassigns it. Depth-bounded so a deeply nested or cyclic payload can't
 * blow the stack. */
const MAX_REDACT_DEPTH = 8;
export function redactDomainsDeep(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') {
    return redactDomainsFromText(value);
  }
  if (depth >= MAX_REDACT_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    const target = toWritableClone(value as unknown[]);
    const record = target as unknown as Record<string, unknown>;
    for (let i = 0; i < target.length; i += 1) {
      redactOwnDataProperty(record, String(i), (child) => redactDomainsDeep(child, depth + 1));
    }
    return target;
  }
  const target = toWritableClone(value as Record<string, unknown>);
  for (const key of Object.keys(target)) {
    redactOwnDataProperty(target, key, (child) => redactDomainsDeep(child, depth + 1));
  }
  return target;
}

export function redactExceptionValues(
  values: NonNullable<ErrorEvent['exception']>['values'],
): void {
  if (!Array.isArray(values)) {
    return;
  }
  for (const value of values) {
    if (typeof value.value === 'string') {
      value.value = redactDomainsFromText(value.value);
    }
  }
}

export function redactBreadcrumbDomains(breadcrumbs: ErrorEvent['breadcrumbs']): void {
  if (!Array.isArray(breadcrumbs)) {
    return;
  }
  for (const crumb of breadcrumbs) {
    if (typeof crumb.message === 'string') {
      crumb.message = redactDomainsFromText(crumb.message);
    }
  }
}
